import React, {useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {GiftshopPackageData} from 'types'
import {useTranslation} from 'i18n'
import {Button, Icon, ListLazy, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {GiftShopSettingPackageContentProps} from './GiftShopSettingPackageContentProps'
import GiftShopSettingPackageContentItem from './GiftShopSettingPackageContentItem'

const StyledButton = styled(Button)`
  width: 100%;
`

const StyledSectionHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-bottom: 1px solid ${({theme}) => theme.gray_1};
  min-height: ${convertUnit(64)};
  padding: 0 ${convertUnit(20)};
  gap: ${convertUnit(20)};
`

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({theme}) => theme.white_1};
  border-right: 1px solid ${({theme}) => theme.gray_1};
  flex: 1;
  overflow: hidden;

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    border: none;
  }
`

export default function GiftShopSettingPackageContent({
  showTitle,
  statePackages,
  onCreatePackage,
  onEditPackage,
  loadData,
}: GiftShopSettingPackageContentProps) {
  const {translate} = useTranslation()

  const handleRenderEmptyPackage = useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          textAlign: 'center',
          flex: 1,
          padding: `0 ${convertUnit(20)}`,
        }}>
        <Icon size={32} type="collection" color="primary_5" />
        <Paragraph fontSize="xl" fontWeight="bold">
          {translate('giftShop:settingPackageEmptyTitle')}
        </Paragraph>
        <Paragraph fontSize="m">
          {translate('giftShop:settingPackageDescription_1')}
        </Paragraph>
      </div>
    ),
    [translate],
  )

  const handleRenderItem = useCallback(
    (pkg: GiftshopPackageData) => (
      <GiftShopSettingPackageContentItem
        key={pkg.id}
        package={pkg}
        onClickPackage={() => {
          onEditPackage(pkg)
        }}
      />
    ),
    [onEditPackage],
  )

  const handleRenderPackages = useMemo(
    () => (
      <ListLazy
        keyExtractor={(pkg) => pkg.id}
        loadData={loadData}
        renderItem={handleRenderItem}
        listEmptyElement={handleRenderEmptyPackage}
        stateData={statePackages}
      />
    ),
    [handleRenderEmptyPackage, handleRenderItem, loadData, statePackages],
  )

  return (
    <StyledSection>
      <StyledSectionHeader>
        {showTitle && (
          <>
            <Icon type="collection" />
            <Paragraph fontSize="l" fontWeight="medium">
              {translate('global:packages')}
            </Paragraph>
          </>
        )}
        <StyledButton
          label={translate('giftShop:settingPackageCreateTitle')}
          onClick={() => {
            onCreatePackage()
          }}
        />
      </StyledSectionHeader>
      {handleRenderPackages}
    </StyledSection>
  )
}
